import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useApi } from "../../api/ApiProvider";
import {
  BankAccountWithMandatesDto,
  ListSepaTransactionsDirectionEnum,
  ListSepaTransactionsStatusEnum,
  ListSepaExportBatchesDirectionEnum,
  ListSepaExportBatchesStatusEnum,
  SepaTransactionPageDto,
  SepaExportBatchPageDto,
  SepaExportBatchResponseDto,
} from "../../api-client";
import { sepaKeys } from "./sepa.keys";

/**
 * SEPA read hooks (react-query), wrapping the generated `SepaApi` exposed via
 * `useApi().sepa`. The proxied client already unwraps `.data`, so the query
 * functions resolve directly to the payload.
 */

/** A patient's bank accounts, each enriched with its mandate summaries. */
export const useSepaBankAccounts = (patientId: number, enabled = true) => {
  const { sepa } = useApi();

  return useQuery<BankAccountWithMandatesDto[]>({
    queryKey: sepaKeys.bankAccounts.forPatient(patientId),
    queryFn: () =>
      sepa.getSepaBankAccountsForPatient(patientId) as Promise<BankAccountWithMandatesDto[]>,
    enabled: enabled && !!patientId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export interface SepaTransactionFilters {
  accountId?: string;
  direction?: ListSepaTransactionsDirectionEnum;
  status?: ListSepaTransactionsStatusEnum;
  /** Global spotlight search (account-scoped; ignores direction/status). */
  searchStr?: string;
  limit?: number;
}

/** Keyset-paginated SEPA transactions with optional account/direction/status filters. */
export const useSepaTransactions = (filters: SepaTransactionFilters = {}, enabled = true) => {
  const { sepa } = useApi();
  const { accountId, direction, status, searchStr, limit = 15 } = filters;

  return useInfiniteQuery<SepaTransactionPageDto>({
    queryKey: sepaKeys.transactions.list({ accountId, direction, status, searchStr, limit }),
    queryFn: ({ pageParam }) =>
      sepa.listSepaTransactions(
        pageParam as string | undefined,
        limit,
        accountId,
        direction,
        status,
        searchStr,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
    refetchOnWindowFocus: false,
  });
};

/**
 * A single export batch with its lifecycle fields and included transactions.
 * Lazy on purpose: pass `enabled = true` only when the timeline dialog opens so the
 * endpoint is never hit until the user clicks an export.
 */
export const useSepaExportBatch = (batchId: string | null, enabled = false) => {
  const { sepa } = useApi();

  return useQuery<SepaExportBatchResponseDto>({
    queryKey: sepaKeys.exportBatches.detail(batchId ?? ""),
    queryFn: () =>
      sepa.getSepaExportBatch(batchId as string) as Promise<SepaExportBatchResponseDto>,
    enabled: enabled && !!batchId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
};

export interface SepaExportBatchFilters {
  accountId?: string;
  direction?: ListSepaExportBatchesDirectionEnum;
  status?: ListSepaExportBatchesStatusEnum;
  /** Search by batch number or creator name. */
  searchStr?: string;
  limit?: number;
}

/** Keyset-paginated export batches (the "Exporte" tab). */
export const useSepaExportBatches = (filters: SepaExportBatchFilters = {}, enabled = true) => {
  const { sepa } = useApi();
  const { accountId, direction, status, searchStr, limit = 15 } = filters;

  return useInfiniteQuery<SepaExportBatchPageDto>({
    queryKey: sepaKeys.exportBatches.list({ accountId, direction, status, searchStr, limit }),
    queryFn: ({ pageParam }) =>
      sepa.listSepaExportBatches(
        pageParam as string | undefined,
        limit,
        accountId,
        direction,
        status,
        searchStr,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
    refetchOnWindowFocus: false,
  });
};
